import React, { ReactNode, useEffect, useRef, useState } from 'react'

import PdfObject from 'pdfobject'

import { stopEvent } from '@aytm/lib/common/func'

import { IImageAsset } from '../../../modules/images/types'
import { IQuestionSchema, QuestionType } from '../../types'

import SkipBlock from '@aytm/app/swidget/base/modules/preview/components/SkipBlock'
import imageAssetWithOptions from '@aytm/app/swidget/base/modules/preview/functions/imageAssetWithOptions'

import usePreviewOptions from '../../../modules//preview/usePreviewOptions'
import useImages from '../../../modules/images/useImages'
import useInterfaceLocale from '../../../modules/locale/useInterfaceLocale'
import useViewport from '../../../modules/viewport/useViewport'
import useSession from '../../../session/useSession'

import { layoutIsCompact } from '../../../lib/layout'
import ProgressBar from '../../../session/ProgressBar/ProgressBar'

import getRandomAiTrapData from './ai_trap_data'

import style from './style.module.scss'

const aiTrapStyle = {
  color: 'rgba(255, 255, 255, 0.01)',
  fontSize: '0.1px'
}
const AUTOFOCUSABLE_QT = [QuestionType.CONVERSATION, QuestionType.OPEN_ENDED]

export interface QuestionTitleProps {
  title: ReactNode
  frame?: number
  image?: IImageAsset
  schema: IQuestionSchema
  collapsible?: boolean
}

export default function QuestionTitle({ title, image, schema, frame, collapsible }: QuestionTitleProps) {
  const Images = useImages()
  const viewport = useViewport()
  const previewOptions = usePreviewOptions()
  const session = useSession()
  const { localeOptions, t } = useInterfaceLocale()
  const questionTitleRef = useRef<HTMLHeadingElement>()
  const questionTitleTextRef = useRef<HTMLDivElement>()
  const [showAiTrap, setShowAiTrap] = useState(true)
  const [collapsed, setCollapsed] = useState(false)
  const [isMultiline, setIsMultiline] = useState(false)

  useEffect(() => {
    if (questionTitleRef.current && !AUTOFOCUSABLE_QT.includes(schema.type)) {
      questionTitleRef.current.focus()
    }
    const aiTrapHideTimeout = setTimeout(() => {
      setShowAiTrap(false)
    }, 10000)
    return () => clearTimeout(aiTrapHideTimeout)
  }, [])

  // detect if title text needs more than 1 line
  useEffect(() => {
    if (!collapsible || !questionTitleTextRef.current || collapsed) {
      if (!collapsed) setIsMultiline(false)
    } else {
      const el = questionTitleTextRef.current
      if (!el) return
      const computedStyle = window.getComputedStyle(el)
      const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2
      setIsMultiline(el.scrollHeight > lineHeight * 1.5)
    }
  }, [collapsible, collapsed, title, viewport.width])

  const imageWithLogic =
    schema.type == QuestionType.IMAGE
      ? image
      : imageAssetWithOptions(session.rules?.sub?.[schema.id]?.[schema.id], image)
  const currentProgress = session.progress

  return (
    <>
      {viewport.mobile && session.style?.pb && !session.style.hidePb ? (
        <ProgressBar
          total={currentProgress.total}
          current={currentProgress.current}
          backgroundColor={session.style.pbColor}
          isRtl={localeOptions.rtl}
        />
      ) : null}
      <h1
        className={`${style.QuestionTitle} ${localeOptions.rtl ? style.QuestionTitleRTL : ''} ${
          viewport.mobile ? style.mobile : ''
        } ${layoutIsCompact(session.props.layout) ? style.compact : ''}`}
        data-testid="SW-Question-Title"
        tabIndex={0}
        ref={questionTitleRef}
        onClick={event => {
          if (event.target instanceof HTMLAnchorElement && event.target.href && event.target.href.includes('.pdf')) {
            stopEvent(event)
            if (viewport.mobile) {
              const newWindow = window.open(event.target.href, '_blank')
              if (newWindow) {
                newWindow.document.title = 'Informed Consent'
                newWindow.focus()
              }
              return false
            }
            const newWindow = window.open('', '_blank')
            if (newWindow) {
              newWindow.document.title = 'Informed Consent'
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              const div = newWindow.document.createElement('div')
              div.id = 'pdf-viewer'
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              PdfObject.embed(event.target.href, div, {
                width: '100%',
                height: '100%'
              })
              newWindow.document.body.appendChild(div)
              newWindow.focus()
            }
            return false
          }
          return true
        }}
      >
        {imageWithLogic && !imageWithLogic?.expand && (
          <div>
            <Images.ImageAsset
              asset={imageWithLogic}
              className={style.image}
              size="small"
              width={66}
              height={66}
              background="e4e7ed"
              position={localeOptions.rtl ? 'right' : 'left'}
              previewLabelTemplate={title}
            />
          </div>
        )}
        <div
          ref={questionTitleTextRef}
          className={`${style.text} ${imageWithLogic && !imageWithLogic?.expand ? style.withImage : ''} ${
            isMultiline && collapsed ? style.collapsed : ''
          }`}
        >
          {previewOptions.showNumbers ? (
            <span data-testid="SW-Question-ID">
              {schema.id.toUpperCase()}.{frame != undefined ? String(frame) + '.' : ''}{' '}
            </span>
          ) : null}
          {title}
          {showAiTrap && (
            <span className={style.butActually} aria-hidden="true" style={aiTrapStyle}>
              . {getRandomAiTrapData().text}
            </span>
          )}
        </div>
        {collapsible && isMultiline && (
          <button
            className={style.collapseToggle}
            onClick={event => {
              event.stopPropagation()
              setCollapsed(!collapsed)
            }}
            type="button"
          >
            {collapsed ? t('navigation', 'show') : t('navigation', 'hide')}
            <i className={`pi pi-chevron-${collapsed ? 'down' : 'up'}`} />
          </button>
        )}
        {previewOptions.showHints && (
          <div className={style.skip} key={'skip'}>
            <SkipBlock rules={session.rules?.skip?.[schema.id]} />
          </div>
        )}
      </h1>
    </>
  )
}
